import { useState, useEffect, useContext, useMemo } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { AppContextProvider, AppContext } from "./AppContext";
import HomePage from "./pages/HomePage";
import LandingPage from "./pages/LandingPage";
import AboutPage from "./pages/AboutPage";
import LoginDialog from "./components/LoginDialog";
import SignupDialog from "./components/SignupDialog";
import "./styles/global.scss";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import "firebase/compat/firestore";
import firebaseConfig from "./firebaseConfig";
import { getAuth, onAuthStateChanged } from "firebase/auth";

// Initialize Firebase if not already initialized
if (!firebase.apps.length) {
  firebase.initializeApp(firebaseConfig);
}

function AppContent() {
  const { isDarkMode } = useContext(AppContext);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  const [signupDialogOpen, setSignupDialogOpen] = useState(false);

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: isDarkMode ? 'dark' : 'light',
          primary: {
            main: isDarkMode ? "#000000" : "#1976d2",
          },
          secondary: {
            main: isDarkMode ? "#ffffff" : "#dc004e",
          },
          background: {
            default: isDarkMode ? "#000000" : "#ffffff",
            paper: isDarkMode ? "#1a1a1a" : "#f5f5f5",
          },
          text: {
            primary: isDarkMode ? "#ffffff" : "#000000",
            secondary: isDarkMode ? "#cccccc" : "#666666",
          },
        },
        typography: {
          fontFamily: "'Gochi Hand', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
        },
      }),
    [isDarkMode]
  );

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const handleLoginClick = () => {
    setLoginDialogOpen(true);
  };

  const handleSignupClick = () => {
    setSignupDialogOpen(true);
  };

  if (loading) {
    return null; // Or a loading spinner
  }

  return (
    <ThemeProvider theme={theme}>
      <Router>
        <div className="app-container">
          <Routes>
            <Route
              path="/"
              element={
                user ? (
                  <HomePage />
                ) : (
                  <LandingPage
                    onLoginClick={handleLoginClick}
                    onSignupClick={handleSignupClick}
                  />
                )
              }
            />
            <Route path="/about" element={<AboutPage />} />
          </Routes>
          <LoginDialog
            open={loginDialogOpen}
            onClose={() => setLoginDialogOpen(false)}
          />
          <SignupDialog
            open={signupDialogOpen}
            onClose={() => setSignupDialogOpen(false)}
          />
        </div>
      </Router>
    </ThemeProvider>
  );
}

function App() {
  return (
    <AppContextProvider>
      <AppContent />
    </AppContextProvider>
  );
}

export default App;
